<script setup lang="ts" >
import { TodoStatus, type Todos } from "@classes-service-types/index";
import { useVuelidate } from "@vuelidate/core";
import { helpers, maxLength, requiredIf } from "@vuelidate/validators";
import { useVModel } from "@vueuse/core";
import { format, isValid, parse } from "date-fns";
import Calendar from "primevue/calendar";
import Dialog from "primevue/dialog";
import Dropdown, { type DropdownChangeEvent } from "primevue/dropdown";
import RadioButton from "primevue/radiobutton";
import { computed, type Ref, reactive, ref, toRaw, watch } from "vue";
import type {
  SessionTodoForm,
  WysiwygContent,
} from "@/components/program-manager/sessions/components/TodosSection/types";
import { HTTP_URL_PREFIX, httpUrl, toStoredUrl } from "@/util/httpUrl";
import { hasTiptapText } from "@/util/tiptap";
import WYSIWYG from "../../forms/SharedComponents/WYSIWYG/WYSIWYG.vue";
import BaseButton from "../../shared/Button/BaseButton.vue";
import ErrorMessage from "../../shared/ErrorMessage.vue";
import AddFormIcon from "../../shared/icons/AddFormIcon.vue";
import CloseIconLarge from "../../shared/icons/CloseIconLarge.vue";
import { useTodos } from "./composables/useTodos";
import {
  MAX_INSTRUCTIONS_LENGTH,
  MAX_URL_LENGTH,
  PLACEMENT_OPTIONS,
  RANKING_OPTIONS,
  SUPPORTING_URL_TODO_IDS,
} from "./constants";
import { dialogPt, dropdownPt, radioButtonPt } from "./style";
import type { CreateEditTodosModalProps, GetTodosParams, RadioOption } from "./types";

const props = withDefaults(defineProps<CreateEditTodosModalProps>(), {
  edit: false,
  open: false,
  saving: false,
  closeOnSave: true,
  modelValue: () => ({}) as SessionTodoForm,
  currentTodos: () => [],
});

const emit = defineEmits<{
  (e: "update:modelValue", value: SessionTodoForm): void;
  (e: "closeModal"): void;
  (e: "openModal"): void;
  (e: "removeTodo"): void;
  (e: "saveTodo"): void;
}>();

const todo = useVModel(props, "modelValue", emit);
const originalTodo = ref<SessionTodoForm>(structuredClone(toRaw(todo.value)));
const removeConfirmModalOpen = ref<boolean>(false);

/**
 * The modal stays mounted across opens, so re-snapshot the incoming row each
 * time it opens. Without this, Cancel would revert to whatever row was first
 * opened in this session, not the row currently being edited.
 */
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      originalTodo.value = structuredClone(toRaw(todo.value));
    } else {
      v$.value.$reset();
      instructionsCharCount.value = 0;
    }
  },
);

/**
 * Fetch todos and filter them to only show active todos that are not already selected
 */
const todoParams = reactive<GetTodosParams>({
  limit: 60,
});
const { todos, todosIsLoading } = useTodos(todoParams);

const priorityOptions = computed<RadioOption[]>(() => RANKING_OPTIONS.filter((option) => option.value !== 0));

const filteredTodos = computed(() => {
  return (todos.value ?? [])
    .filter((item: Todos) => {
      const isActive = item.status === TodoStatus.Active;
      const isCurrentlyEdited = props.edit && item.id === todo.value?.todo_id;
      const isNotSelected = !props.currentTodos.some(
        (currentTodo: SessionTodoForm) => currentTodo.todo_id === item.id,
      );
      return isActive && (isCurrentlyEdited || isNotSelected);
    })
    .sort((a: Todos, b: Todos) => a.title.localeCompare(b.title));
});

// Resolved against the unfiltered catalog, not filteredTodos: that list drops anything not Active,
// which would blank the title and the field visibility for a to-do archived after it was added.
const selectedTodo = computed<Todos | undefined>(() =>
  (todos.value ?? []).find((item: Todos) => item.id === todo.value.todo_id),
);

const selectedTodoTitle = computed<string>(() => selectedTodo.value?.title ?? props.fallbackTitle ?? "");

// Reads the opening snapshot, not the live value, so clearing the editor cannot hide the field.
const showInstructions = computed<boolean>(
  () => hasTiptapText(selectedTodo.value?.default_instructions) || hasTiptapText(originalTodo.value?.instructions),
);

// Only a catalog default makes it mandatory; text that exists only on this row can be cleared.
const instructionsRequired = computed<boolean>(() => hasTiptapText(selectedTodo.value?.default_instructions));

const showSupportingUrl = computed<boolean>(
  () =>
    SUPPORTING_URL_TODO_IDS.includes(todo.value.todo_id ?? "") || Boolean(originalTodo.value?.supporting_url?.trim()),
);

/**
 * Two-way bridge between todo.due_date (YYYY-MM-DD string for the API) and
 * PrimeVue Calendar's v-model (Date object). Parsed in local time so the
 * displayed day matches what the user picks, regardless of timezone.
 */
const dueDateModel = computed<Date | null>({
  get: () => {
    const raw = todo.value?.due_date;
    if (!raw) return null;
    const parsed = parse(raw, "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed : null;
  },
  set: (value: Date | null) => {
    todo.value.due_date = value ? format(value, "yyyy-MM-dd") : null;
  },
});

/**
 * Character count for the instructions
 */
const instructionsCharCount: Ref<number> = ref(0);

/**
 * Modal event handlers
 */
const closeModal = () => {
  v$.value.$reset();
  instructionsCharCount.value = 0;
  emit("closeModal");
};

const onCancel = () => {
  todo.value = originalTodo.value;
  closeModal();
};

const onDialogVisibleChange = (visible: boolean): void => {
  if (!visible) {
    onCancel();
  }
};

const onRemove = () => {
  emit("removeTodo");
  removeConfirmModalOpen.value = false;
  closeModal();
};

const onSave = async () => {
  v$.value.$touch();
  const isValid = await v$.value.$validate();
  if (!isValid) return;
  todo.value.supporting_url = toStoredUrl(todo.value.supporting_url);
  emit("saveTodo");
  if (props.closeOnSave) {
    closeModal();
  }
};

/**
 * Validation rules
 */
const getCharLengthErrorMessage = (name: string, max_length: number) => {
  return `Oops! ${name} cannot exceed ${max_length} characters`;
};

const rules = computed(() => ({
  todo_id: {
    required: helpers.withMessage("To-Do name is required", requiredIf(props.open)),
  },
  due_date: {
    required: helpers.withMessage("Due date is required", requiredIf(props.open)),
  },
  supporting_url: {
    maxLength: helpers.withMessage(
      getCharLengthErrorMessage("Supporting URL", MAX_URL_LENGTH),
      maxLength(MAX_URL_LENGTH),
    ),
    httpUrl,
  },
  ranking: {
    required: helpers.withMessage("Priority level is required", requiredIf(props.open)),
  },
  instructions: {
    required: helpers.withMessage(
      "Instructions are required",
      (value: WysiwygContent) => !props.open || !instructionsRequired.value || hasTiptapText(value),
    ),
  },
}));

const v$ = useVuelidate(rules, todo);

const resolveDefaultDisplayName = (selectedTodo: Todos | undefined): string =>
  selectedTodo?.default_display_name || selectedTodo?.title || "";

const populateDisplayName = (): void => {
  const selectedTodo = filteredTodos.value?.find((item: Todos) => item.id === todo.value.todo_id);
  if (selectedTodo) {
    todo.value.display_name = resolveDefaultDisplayName(selectedTodo);
  }
};

const onTodoChange = (event: DropdownChangeEvent) => {
  const selectedTodo = filteredTodos.value?.find((item: Todos) => item.id === event.value);
  todo.value.display_name = resolveDefaultDisplayName(selectedTodo);
  todo.value.placement = selectedTodo?.default_placement ?? "Onboarding";
  todo.value.instructions = (selectedTodo?.default_instructions ?? {}) as WysiwygContent;
  todo.value.supporting_url = selectedTodo?.default_supporting_url ?? "";
};

const onSupportingUrlFocus = (): void => {
  if (!todo.value.supporting_url?.trim()) {
    todo.value.supporting_url = HTTP_URL_PREFIX;
  }
};

const onSupportingUrlBlur = (): void => {
  todo.value.supporting_url = toStoredUrl(todo.value.supporting_url) ?? "";
  v$.value.supporting_url.$touch();
};

const onDisplayNameBlur = (): void => {
  if (!todo.value.display_name && todo.value.todo_id) {
    populateDisplayName();
  }
};

watch(
  [filteredTodos, () => props.open],
  () => {
    if (props.open && todo.value.todo_id && !todo.value.display_name) {
      populateDisplayName();
    }
    // Edit mode has no dropdown, so onTodoChange never runs to seed the catalog default.
    if (
      props.open &&
      props.edit &&
      !hasTiptapText(todo.value.instructions) &&
      hasTiptapText(selectedTodo.value?.default_instructions)
    ) {
      todo.value.instructions = selectedTodo.value?.default_instructions as WysiwygContent;
    }
  },
  { immediate: true },
);
</script>

<template>
  <Dialog
    :visible="props.open"
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    modal
    :pt="dialogPt"
    :style="{ width: '56rem', maxWidth: '90vw' }"
    @update:visible="onDialogVisibleChange"
  >
    <template #header>
      <div class="flex flex-row items-center justify-between shrink-0 h-[50px] w-full bg-teal-accent px-5 py-3 text-sm font-bold text-university-primary rounded-t-lg">
        <div class="flex flex-row items-center gap-2">
          <AddFormIcon />
          <span>{{ props.edit ? "Edit To-Do" : "Add To-Do" }}</span>
        </div>
        <button class="h-6 w-6" data-testid="close-modal-button" @click="onCancel">
          <span class="sr-only">Close</span>
          <CloseIconLarge />
        </button>
      </div>
    </template>
    <div class="flex flex-col space-y-6 text-indigo-base p-6 sm:px-10 sm:pt-8 sm:pb-8">
        <div class="flex flex-col">
          <label class="font-semibold"> To-Do Name <span class="text-error-900">*</span> </label>
          <p v-if="props.edit" data-testid="todo-name-readonly">{{ selectedTodoTitle }}</p>
          <Dropdown
            v-else
            id="todo-id"
            v-model="todo.todo_id"
            data-testid="todo-id-input"
            class="flex w-full border-gray-300 !outline-none shadow-sm hover:border-black"
            input-class="placeholder:text-gray-400"
            :pt="dropdownPt"
            :append-to="'self'"
            placeholder="Select"
            filter-placeholder="Search"
            filter
            :loading="todosIsLoading"
            :options="filteredTodos"
            option-label="title"
            option-value="id"
            @change="onTodoChange"
          >
            <template #option="item">
              <div class="py-3 px-5 todo-custom-item">
                {{ item.option.title }}
              </div>
            </template>
          </Dropdown>

          <ErrorMessage
            v-if="!props.edit"
            :vuelidate-instance="v$.todo_id"
            error-classes="flex text-sm error text-error-900 items-center"
          />
        </div>
        <div class="flex flex-col">
          <label class="font-semibold">
            To-Do Display Name
          </label>
          <input
            id="display-name"
            v-model="todo.display_name"
            type="text"
            name="default_display_name"
            class="w-full form-input"
            data-testid="todo-display-name-input"
            @blur="onDisplayNameBlur"
          />
        </div>
        <div class="flex flex-row">
          <div class="flex flex-col w-full">
            <label class="font-semibold"> Due Date <span class="text-error-900">*</span> </label>
            <div class="grid grid-cols-2 gap-10 items-center">
              <div class="w-full">
                <Calendar
                  v-model="dueDateModel"
                  name="due_date"
                  data-testid="todo-due-date-input"
                  date-format="mm/dd/yy"
                  placeholder="mm/dd/yyyy"
                  show-icon
                  icon-display="input"
                  :manual-input="true"
                  :pt="{
                    root: { class: 'flex w-full relative' },
                    input: {
                      class:
                        'w-full h-10 rounded-md border border-gray-300 shadow-sm text-sm bg-white pl-3 pr-9 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-base focus:border-indigo-base',
                    },
                    inputicon: {
                      class: 'absolute top-[50%] -mt-2 right-[0.75rem] text-indigo-base pointer-events-none',
                    },
                  }"
                />
                <ErrorMessage
                  :vuelidate-instance="v$.due_date"
                  error-classes="text-sm text-red-500"
                />
              </div>
              <div class="flex flex-row items-center">
                <input
                  v-model="todo.required"
                  class="h-5 w-5 form-checkbox bg-white border-indigo-base text-indigo-base opacity-60 cursor-default"
                  type="checkbox"
                  disabled
                  data-testid="todo-required-input"
                />
                <label class="font-medium ml-4">
                  This To-Do is required
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="flex flex-row gap-8">
          <div class="flex flex-col flex-1 space-y-2">
            <label for="ranking" class="font-semibold">
              Priority Level <span class="text-error-900">*</span>
            </label>
            <div class="flex flex-wrap gap-10 ml-4">
              <div v-for="option in priorityOptions" :key="option.value" class="flex align-items-center">
                <RadioButton
                  v-model="todo.ranking"
                  :input-id="`ranking-${option.value}`"
                  name="ranking" :value="option.value"
                  :pt="radioButtonPt"
                  data-testid="ranking-input"
                />
                <label :for="`ranking-${option.value}`" class="ml-2 text-indigo-base">{{ option.label }}</label>
              </div>
            </div>
          </div>
          <div class="flex flex-col flex-1 space-y-2">
            <label for="placement" class="font-semibold">
              Appears In
            </label>
            <div class="flex flex-wrap gap-10 ml-4">
              <div v-for="option in PLACEMENT_OPTIONS" :key="option.value" class="flex align-items-center">
                <RadioButton
                  v-model="todo.placement"
                  :input-id="`placement-${option.value}`"
                  name="placement"
                  :value="option.value"
                  :pt="radioButtonPt"
                  disabled
                  data-testid="placement-input"
                />
                <label :for="`placement-${option.value}`" class="ml-2 text-indigo-base">{{ option.label }}</label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showInstructions" data-testid="todo-instructions-section">
          <label class="font-semibold">
            Instructions <span v-if="instructionsRequired" class="text-error-900">*</span>
          </label>
          <WYSIWYG
            :content="todo.instructions"
            :character-limit="MAX_INSTRUCTIONS_LENGTH"
            :show-limit="true"
            :has-error="v$.instructions.$error"
            data-testid="todo-instruction-wysiwyg"
            @update-content="($val) => (todo.instructions = $val)"
            @character-count="(charCount) => (instructionsCharCount = charCount)"
          />

          <ErrorMessage
            :vuelidate-instance="v$.instructions"
            error-classes="text-sm text-red-500"
          />
        </div>
        <div v-if="showSupportingUrl" class="flex flex-col">
          <label for="url" class="font-semibold">
            Supporting URL
          </label>
          <input
            v-model="todo.supporting_url"
            :placeholder="HTTP_URL_PREFIX"
            type="text"
            name="url"
            class="w-full form-input"
            data-testid="todo-url-input"
            @focus="onSupportingUrlFocus"
            @blur="onSupportingUrlBlur"
          />

          <ErrorMessage
            :vuelidate-instance="v$.supporting_url"
            error-classes="text-sm text-red-500"
          />
        </div>
      </div>
    <template #footer>
      <div class="p-6 sm:px-10">
        <div class="flex items-center justify-between gap-2 text-sm">
          <div>
            <BaseButton
              v-if="props.edit"
              class="!mr-0"
              :outlined="true"
              data-test-id="remove-todo-button"
              @click="removeConfirmModalOpen = true"
            >
              <i class="fa fa-trash mr-1"></i>
              Remove
            </BaseButton>
          </div>
          <div class="flex items-center gap-2">
            <BaseButton
              class="!mr-0"
              :outlined="true"
              :disabled="props.saving"
              data-test-id="save-todo-cancel-button"
              @click="onCancel"
              >Cancel</BaseButton
            >
            <BaseButton
              class="!mr-0"
              :filled="true"
              :disabled="props.saving"
              data-test-id="save-todo-button"
              @click="onSave"
              >{{ props.saving ? "Saving..." : props.edit ? "Update" : "Add" }}</BaseButton
            >
          </div>
        </div>
        <div
          v-show="v$.$errors?.length > 0"
          class="flex flex-row w-full justify-end pr-2 text-sm error text-error-900 items-center mt-2"
        >
          Please complete all required fields.
        </div>
      </div>
    </template>
  </Dialog>
  <Dialog
    :visible="removeConfirmModalOpen"
    :closable="false"
    :close-on-escape="false"
    :dismissable-mask="false"
    modal
    :pt="dialogPt"
    :style="{ width: '500px', maxWidth: '90vw' }"
  >
    <template #header>
      <div
        class="flex flex-row items-center justify-start shrink-0 h-[50px] w-full bg-teal-accent px-5 py-3 text-sm font-bold text-university-primary"
      >
        <i class="fa fa-trash"></i>
        <span class="ml-2">REMOVE TO-DO</span>
      </div>
    </template>
    <div class="text-indigo-base px-6 pt-8 pb-6 sm:px-10">
      Are you sure you want to remove {{ selectedTodoTitle }}? Any existing participants will no longer see the To-Do in
      their portal.
    </div>
    <template #footer>
      <div class="flex flex-row justify-center gap-2 px-6 pb-6 sm:px-10">
        <BaseButton
          class="px-7 min-w-[104px]"
          :rounded="false"
          :outlined="true"
          data-test-id="remove-cancel-todo-button"
          @click="removeConfirmModalOpen = false"
          >NO</BaseButton
        >
        <BaseButton
          v-if="props.edit"
          class="min-w-[104px]"
          :rounded="false"
          :filled="true"
          data-test-id="remove-confirm-todo-button"
          @click="onRemove"
          >YES</BaseButton
        >
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.todo-custom-item:hover {
  --tw-bg-opacity: 1;
  background-color: rgba(213, 233, 231, var(--tw-bg-opacity));
  border-radius: 0.5rem;
}
</style>
